import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Stack, Typography } from "@mui/material";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import {
  getEmployeeRequest,
  getEmployeeResponse,
  editEmployeeRequest,
} from "../../../../store/Employees/actions";
import { getLookupsRequest } from "../../../../store/Lookups/actions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { lookupsKeys } from "../../../../utils/Constants";
import { ALPHABETIC_REGEX } from "../../../../utils/Patterns";
import { getRolesRequest } from "../../../../store/Role/actions";
import {
  UIButton,
  UIPhoneField,
  UISelect,
  UITextField,
} from "../../../../components/ui";
import "../Employees.scss";
import "./EditEmployees.scss";

const EditEmployee = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const jobTitlesList = useSelector(
    (state) => state.lookups.general.adminRoles
  );
  const employee = useSelector((state) => state.employees.employee);
  const { list } = useSelector((state) => state.role);
  const { employees, shared } = messages[lang];

  const roleOptions =
    list?.map((role) => ({
      id: role.id,
      name: role.name,
    })) || [];

  const {
    values,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
    setValues,
  } = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone_number: null,
      country_id: null,
      jobTitle: {},
      roles: null,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .matches(ALPHABETIC_REGEX, "nameNotFollowAlphabeticREGEX")
        .required("nameRequired"),
      email: Yup.string().email("validEmailFormat").required("emailRequired"),
      phone_number: Yup.string()
        .min(9, "invalidPhone")
        .max(9, "invalidPhone")
        .nullable(),
      roles: Yup.object({
        id: Yup.number().required("Role is required"),
        name: Yup.string().required("Role is required"),
      }),
    }),
    onSubmit: (formValues) => {
      dispatch(
        editEmployeeRequest({
          id,
          data: {
            name: formValues.name,
            email: formValues.email,
            phone_number: formValues.phone_number || null,
            admin_role_id: formValues.roles.id,
            country_id: 190,
          },
        })
      );
    },
  });

  useEffect(() => {
    dispatch(getRolesRequest({ page: -1 }));
    dispatch(getLookupsRequest([lookupsKeys.adminRoles]));
    dispatch(getEmployeeRequest(id));
  }, [lang, id, dispatch]);

  useEffect(() => {
    if (employee && jobTitlesList && list) {
      setValues({
        name: employee.name || "",
        email: employee.email || "",
        phone_number: employee.phone_number || null,
        jobTitle: jobTitlesList.find((elt) => elt.id === 2) || {},
        roles: list.find((role) => role.id === employee.role?.id) || null,
      });
    }
  }, [employee, jobTitlesList, list, setValues]);

  useEffect(() => {
    if (jobTitlesList && !values.jobTitle.id) {
      setFieldValue("jobTitle", jobTitlesList.find((elt) => elt.id === 2));
    }
  }, [jobTitlesList, setFieldValue, values.jobTitle.id]);

  useEffect(() => {
    return () => {
      dispatch(getEmployeeResponse(null));
    };
  }, [dispatch]);

  return (
    <div className="edit-employee-page">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.employees,
              label: employees.employeesList,
            },
            {
              path: "",
              label: employees.editEmployee,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="d-flex justify-content-between align-items-center">
          <Typography variant="h5">{employees.editEmployeeTitle}</Typography>
        </Box>
      </div>

      <form className="edit-employee-page__form" noValidate onSubmit={handleSubmit}>
        <div className="edit-employee-page__section">
          <div className="edit-employee-page__grid">
            <UITextField
              className="edit-employee-page__field"
              id="name"
              name="name"
              label={employees.addProjectManager.labels.name}
              placeholder={employees.addProjectManager.placeholders.name}
              required
              value={values.name}
              onValueChange={(value) => {
                setFieldTouched("name", true);
                setFieldValue("name", value);
              }}
              onBlur={() => setFieldTouched("name", true)}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name ? errors.name : ""}
            />
            <UITextField
              className="edit-employee-page__field"
              id="email"
              name="email"
              label={employees.addProjectManager.labels.email}
              placeholder={employees.addProjectManager.placeholders.email}
              required
              disabled
              value={values.email}
              onValueChange={(value) => {
                setFieldTouched("email", true);
                setFieldValue("email", value);
              }}
              onBlur={() => setFieldTouched("email", true)}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email ? errors.email : ""}
            />
          </div>

          <div className="edit-employee-page__grid">
            <UIPhoneField
              className="edit-employee-page__field"
              name="phone_number"
              label={employees.addProjectManager.labels.mobileNumber}
              country="sa"
              countryCode={employee?.dial_code || "966"}
              disableDropdown
              enableSearch={false}
              countryCodeEditable={false}
              value={values.phone_number || ""}
              onChange={(phoneValue, country) => {
                setFieldTouched("phone_number", true);
                setValues({
                  ...values,
                  phone_number: phoneValue,
                  country_id: country?.id,
                });
              }}
              onBlur={() => setFieldTouched("phone_number", true)}
              error={Boolean(touched.phone_number && errors.phone_number)}
              helperText={
                touched.phone_number ? errors.phone_number : ""
              }
            />
            <UISelect
              className="edit-employee-page__field"
              id="roles"
              name="roles"
              label={shared.role}
              placeholder={shared.select}
              options={roleOptions}
              value={values.roles}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(value) => {
                setFieldTouched("roles", true);
                setFieldValue("roles", value);
              }}
              onBlur={() => setFieldTouched("roles", true)}
              error={Boolean(touched.roles && errors.roles)}
              helperText={touched.roles ? errors.roles : ""}
            />
          </div>
        </div>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          className="edit-employee-page__actions"
        >
          <UIButton
            type="button"
            variant="outlined"
            color="inherit"
            onClick={() => history.push(ROUTE_PATHS.employees)}
          >
            {shared.cancel}
          </UIButton>
          <UIButton
            type="submit"
            appearance="gradient"
            disabled={!dirty || !isValid}
          >
            {shared.save}
          </UIButton>
        </Stack>
      </form>
    </div>
  );
};

export default EditEmployee;
